import React, { useContext, useEffect, useState } from "react";
import { Box, Typography, Tooltip, Select, MenuItem, FormControl } from "@mui/material";
import { BusinessContext } from '../context/BusinessContext';
import { ReviewsContext } from '../context/ReviewsContext';
import { colors } from '../utils/themeUtils';

const ReviewsOverview = () => {
  const { currencySymbol, selectedBusinesses } = useContext(BusinessContext);
  const { 
    overviewStats,
    isOverviewLoading,
    overviewError,
    targetRatingData,
    isTargetRatingLoading,
    targetRatingError,
    timeframe, 
    view, 
    selectedCategory,
    selectedSubject,
    selectedMonthRange,
    selectedSources,
    fetchOverviewStats,
    fetchTargetRatingData
  } = useContext(ReviewsContext);
  
  const numSelectedBusinesses = selectedBusinesses?.length || 0;

  // State for target rating selection
  const [selectedTargetRating, setSelectedTargetRating] = useState(null);
  const [shouldAutoSelect, setShouldAutoSelect] = useState(true);
  const [lastHeaderRating, setLastHeaderRating] = useState(null);
  const [waitingForFreshData, setWaitingForFreshData] = useState(false);

  // Generate target rating options based on overall business rating (all-time, but source-filtered)
  const getTargetRatingOptions = () => {
    // Only show options for single business scenarios
    if (numSelectedBusinesses > 1 || !targetRatingData) {
      return [];
    }
    
    // Use the header rating from target rating data (timeframe-independent)
    const currentRating = targetRatingData.header_rating;
    
    // If no rating or already 5.0 or very close, no options needed
    if (!currentRating || currentRating >= 4.95) return [];
    
    // Get the current rating rounded to 1 decimal place, then add 0.1 for minimum target
    const currentRoundedToOneDecimal = Math.round(currentRating * 10) / 10;
    const minTarget = Math.round((currentRoundedToOneDecimal + 0.1) * 10) / 10; // Add 0.1 and round to avoid floating point issues
    const options = [];
    
    for (let rating = minTarget; rating <= 5.0; rating += 0.1) {
      // Round to avoid floating point precision issues
      const roundedRating = Math.round(rating * 10) / 10;
      options.push(roundedRating);
    }
    
    return options;
  };

  // Handle target rating selection change
  const handleTargetRatingChange = (event) => {
    const newTarget = parseFloat(event.target.value);
    setSelectedTargetRating(newTarget);
    setShouldAutoSelect(false); // Prevent auto-reset when user makes manual selection
    
    // The useEffect with selectedTargetRating dependency will handle the refetch
  };

  // Reset target rating when dependencies change (especially sources, which changes the displayed rating)
  useEffect(() => {
    setSelectedTargetRating(null);
    setShouldAutoSelect(true); // Allow auto-selection when sources change
    setLastHeaderRating(null); // Reset to detect rating changes
    setWaitingForFreshData(true); // Wait for fresh data to avoid flash
  }, [selectedBusinesses, selectedSources]); // Remove timeframe, view, selectedCategory, selectedSubject, selectedMonthRange - target rating should be independent of these

  // Set default target rating when targetRatingData loads or rating changes
  useEffect(() => {
    if (targetRatingData) {
      const currentHeaderRating = targetRatingData.header_rating;
      const options = getTargetRatingOptions();
      const ratingChanged = lastHeaderRating !== null && Math.abs(lastHeaderRating - currentHeaderRating) > 0.01;
      const hasFreshData = !waitingForFreshData || ratingChanged;
      
      if (options.length > 0) {
        // Auto-select if:
        // 1. We should auto-select (source change), OR
        // 2. Current selection is invalid for new options, OR  
        // 3. Overall rating changed significantly (different source)
        // BUT only if we have fresh data or aren't waiting for it
        const currentIsValid = selectedTargetRating && options.includes(selectedTargetRating);
        const shouldSetTarget = (shouldAutoSelect || !currentIsValid || ratingChanged) && hasFreshData;
        
        if (shouldSetTarget) {
          setSelectedTargetRating(options[0]);
          setWaitingForFreshData(false); // We got fresh data and set the target
        }
        
        // Always disable auto-select after processing, whether we selected or not
        if (shouldAutoSelect && hasFreshData) {
          setShouldAutoSelect(false);
        }
      } else {
        // No options available, reset selection
        setSelectedTargetRating(null);
        if (shouldAutoSelect) {
          setShouldAutoSelect(false);
        }
        setWaitingForFreshData(false);
      }
      
      // Update last overall rating if we processed this data
      if (hasFreshData) {
        setLastHeaderRating(currentHeaderRating);
      }
    }
  }, [targetRatingData, shouldAutoSelect, selectedTargetRating, lastHeaderRating, waitingForFreshData]);

  // Effect for timeframe-dependent overview data (Reviews, Average)
  useEffect(() => {
    if (selectedBusinesses && selectedBusinesses.length > 0) {
      // Add a small delay to prevent simultaneous API calls with other components
      const timer = setTimeout(() => {
        fetchOverviewStats(selectedTargetRating); // Pass target rating back to overview stats
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [selectedBusinesses, timeframe, view, selectedCategory, selectedSubject, selectedMonthRange, selectedSources, selectedTargetRating, fetchOverviewStats]); // Add selectedTargetRating back

  // Effect for timeframe-independent target rating data (only for header rating and options)
  useEffect(() => {
    if (selectedBusinesses && selectedBusinesses.length > 0) {
      // Add a small delay to prevent simultaneous API calls with other components
      const timer = setTimeout(() => {
        fetchTargetRatingData(); // No target rating parameter needed for header rating
      }, 100); // Slightly different delay to avoid conflicts
      return () => clearTimeout(timer);
    }
  }, [selectedBusinesses, selectedSources, fetchTargetRatingData]); // Remove selectedTargetRating since calculations come from overview stats

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return Number(num).toFixed(1);
  };

  const formatImpact = (impact) => {
    if (impact === undefined || impact === null) return '0.0';
    
    const absImpact = Math.abs(impact);
    
    if (absImpact === 0) return '0.0';
    
    if (absImpact >= 0.001) {
      let formatted = absImpact.toFixed(3);
      if (formatted.includes('.')) {
        formatted = formatted.replace(/0+$/, '');
        if (formatted.endsWith('.')) {
          formatted += '0';
        }
      }
      return formatted;
    }
    
    const scientificNotation = absImpact.toExponential();
    
    const matches = scientificNotation.match(/^(\d)\.?(\d*)?e-(\d+)$/);
    
    if (matches) {
      const firstDigit = matches[1];
      const exponent = parseInt(matches[3], 10);
      
      let result = '0.';
      for (let i = 1; i < exponent; i++) {
        result += '0';
      }
      result += firstDigit;
      
      return result;
    }
    
    let fallbackFormatted = absImpact.toFixed(3);
    if (fallbackFormatted.includes('.')) {
        fallbackFormatted = fallbackFormatted.replace(/0+$/, '');
        if (fallbackFormatted.endsWith('.')) {
          fallbackFormatted += '0';
        }
      }
    return fallbackFormatted;
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '0';
    
    if (Math.abs(value) < 1) {
      return Math.abs(value).toFixed(2);
    }
    
    return Math.abs(value).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const getTimeframeDays = () => {
    if (selectedMonthRange && selectedMonthRange.start) {
      return { 
        text: selectedMonthRange.start.toLocaleString('default', { month: 'long', year: 'numeric' }), 
        isDuration: false 
      };
    }
    if (timeframe === "all") {
      return { text: "All Time", isDuration: false };
    } else {
      return { text: `Last ${timeframe} days`, isDuration: true };
    }
  };
  
  const hasSpecificFilters = selectedCategory || selectedSubject;

  const getFilterDescriptionText = () => {
    const parts = [];
    if (selectedCategory) {
      parts.push(`Category: "${selectedCategory}"`);
    }
    if (selectedSubject) {
      parts.push(`Subject: "${selectedSubject}"`);
    }
    return parts.join(', ');
  };

  // Only show global loading for target rating on initial load or errors that prevent any display
  if (isTargetRatingLoading && !targetRatingData) {
    return <Typography sx={{p: { xs: 2, md: 0 }}}>Loading overview...</Typography>;
  }

  if (targetRatingError && !targetRatingData) {
    return <Typography color="error" sx={{p: { xs: 2, md: 0 }}}>Error loading overview: {targetRatingError}</Typography>;
  }

  if (!overviewStats && !isOverviewLoading && numSelectedBusinesses === 0) {
    return (
      <Box sx={{ height: '100%', p: { xs: 2, md: 0 }, overflow: 'auto' }}>
         <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            Select a business to see its overview.
         </Typography>
      </Box>
    )
  }
  
  if (!overviewStats && !isOverviewLoading && numSelectedBusinesses > 0) {
      return (
          <Box sx={{ height: '100%', p: { xs: 2, md: 0 }, overflow: 'auto' }}>
              <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                  No overview data available.
                  {overviewError ? ` Error: ${overviewError}` : " This might be due to active filters or an issue fetching data."}
              </Typography>
          </Box>
      );
  }

  const {
    review_count,
    average_rating,
    rating_change_sum,
    calculated_revenue_change,
    is_all_reviews_for_period,
    total_reviews_for_single_business,
    estimated_days_to_target,
    needed_reviews_for_next_rating,
    next_rating_target,
    current_overall_rating_for_single_business,
    estimated_months_to_target,
    review_velocity_per_month
  } = overviewStats || {};

  // Use target rating data from separate API only for target rating options and header rating
  // The actual calculations come from overview stats which gets the target rating parameter

  return (
    <Box sx={{ 
      height: '100%',
      p: { xs: 2, md: 0 },
      overflow: 'auto'
    }}>
      {review_count === 0 ? (
        <>
          {numSelectedBusinesses <= 1 && (
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              No reviews received for{' '}
              <Box component="span" sx={{ fontWeight: 'bold', color: 'black' }}>
                {getTimeframeDays().text}
              </Box>
              .
              {hasSpecificFilters && (
                <Typography variant="body2" sx={{ fontSize: '0.9em', color: 'text.secondary', mt: 0.5 }}>
                  Filters: {getFilterDescriptionText()}
                </Typography>
              )}
            </Typography>
          )}
          {numSelectedBusinesses > 1 && (
             <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                No reviews found for the selected businesses matching the current filters and timeframe.
             </Typography>
          )}
        </>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {numSelectedBusinesses <= 1 && current_overall_rating_for_single_business < 4.95 && needed_reviews_for_next_rating > 0 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'baseline', ml: 0, mt: 1 }}>
                <Box sx={{ width: '140px', flexShrink: 0 }}>
                  <Tooltip 
                    title="Select your desired rating goal to see how many 5-star reviews are needed"
                    arrow
                    placement="top"
                  >
                    <Typography variant="body1" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', fontSize: { xs: '1rem', md: '1.125rem' }, textDecoration: 'underline dotted', cursor: 'help' }}>
                      Target Rating:
                    </Typography>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                  {getTargetRatingOptions().length > 0 ? (
                    <FormControl size="small" sx={{ mr: 1, minWidth: 80 }}>
                      <Select
                        value={(() => {
                          const targetOptions = getTargetRatingOptions();
                          const currentValue = selectedTargetRating || next_rating_target;
                          if (!currentValue) return '';
                          
                          // Round the current value and check if it exists in options
                          const roundedValue = Math.round(currentValue * 10) / 10;
                          return targetOptions.includes(roundedValue) ? roundedValue : '';
                        })()}
                        onChange={handleTargetRatingChange}
                        displayEmpty
                        sx={{
                          fontSize: { xs: '1rem', md: '1.125rem' },
                          fontWeight: 'bold',
                          color: 'primary.main',
                          '& .MuiSelect-select': {
                            paddingTop: 0.5,
                            paddingBottom: 0.5,
                          }
                        }}
                      >
                        {getTargetRatingOptions().map((rating) => (
                          <MenuItem key={rating} value={rating}>
                            {formatNumber(rating)} ★
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: { xs: '1rem', md: '1.125rem' }, mr: 1 }}>
                      {formatNumber(next_rating_target)} ★:
                    </Typography>
                  )}
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: { xs: '1rem', md: '1.125rem' } }}>
                    {needed_reviews_for_next_rating}
                  </Typography>
                  <Typography variant="body1" sx={{ ml: 0.5, color: 'text.secondary', fontSize: { xs: '1rem', md: '1.125rem' } }}>
                    5-star reviews
                  </Typography>
                </Box>
              </Box>

              {estimated_months_to_target && estimated_months_to_target > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'baseline', ml: 0, mt: 1 }}>
                  <Box sx={{ width: '140px', flexShrink: 0 }}>
                    <Tooltip 
                      title="Based on review velocity from the last 3 months, assuming all future reviews are 5-star and current issues are resolved"
                      arrow
                      placement="top"
                    >
                      <Typography variant="body1" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', fontSize: { xs: '1rem', md: '1.125rem' }, textDecoration: 'underline dotted', cursor: 'help' }}>
                        Est. timeframe:
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: { xs: '1rem', md: '1.125rem' } }}>
                      {estimated_months_to_target < 1 
                        ? `${Math.ceil(estimated_months_to_target * 30.44)} days`
                        : estimated_months_to_target < 12
                        ? `${estimated_months_to_target.toFixed(1)} months`
                        : `${(estimated_months_to_target / 12).toFixed(1)} years`
                      }
                    </Typography>
                    {review_velocity_per_month && (
                      <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.85em' }}>
                        ({review_velocity_per_month.toFixed(1)} reviews/month)
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </>
          )}

          <Typography variant="body2" sx={{ fontSize: { xs: '1rem', md: '1.125rem' }, mt: 1, fontWeight: 'bold' }}>
             {getTimeframeDays().text}:
          </Typography>

          {isOverviewLoading ? (
            <Typography variant="body1" sx={{ mt: 1, color: 'text.secondary' }}>Loading timeframe data...</Typography>
          ) : overviewError ? (
            <Typography variant="body1" sx={{ mt: 1, color: 'error.main' }}>Error: {overviewError}</Typography>
          ) : overviewStats ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'baseline', ml: 0, mt: 1 }}>
                <Box sx={{ width: '140px', flexShrink: 0 }}>
                  <Tooltip 
                    title="Total number of reviews received during the selected time period"
                    arrow
                    placement="top"
                  >
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: { xs: '1rem', md: '1.125rem' }, textDecoration: 'underline dotted', cursor: 'help' }}>Reviews:</Typography>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.125rem' } }}>
                    {review_count}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'baseline', ml: 0, mt: 1 }}>
                <Box sx={{ width: '140px', flexShrink: 0 }}>
                  <Tooltip 
                    title={view === "Sales" 
                      ? "Average rating and revenue impact during the selected time period"
                      : "Average rating and impact on overall business rating during the selected time period"
                    }
                    arrow
                    placement="top"
                  >
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: { xs: '1rem', md: '1.125rem' }, textDecoration: 'underline dotted', cursor: 'help' }}>Average:</Typography>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 0.5, fontSize: { xs: '1rem', md: '1.125rem' } }}>
                    {formatNumber(average_rating)} ★
                  </Typography>
                  {(!is_all_reviews_for_period || hasSpecificFilters) && rating_change_sum !== 0 && (
                    <Box sx={{ display: 'inline-flex', alignItems: 'baseline', ml: 0.5 }}>
                      <Typography sx={{ color: rating_change_sum >= 0 ? colors.positiveGreen : colors.negativeRed, mr: 0.5, fontSize: { xs: '1rem', md: '1.125rem' } }}>
                        {rating_change_sum > 0 ? '↑' : '↓'}
                      </Typography>
                      <Typography sx={{ color: rating_change_sum >= 0 ? colors.positiveGreen : colors.negativeRed, fontSize: { xs: '1rem', md: '1.125rem' } }}>
                        {(view === "Sales" && calculated_revenue_change !== null) ? (calculated_revenue_change >= 0 ? '+' : '-') : (rating_change_sum > 0 ? '+' : '-')}
                        {view === "Sales" && calculated_revenue_change !== null
                          ? `${currencySymbol}${formatCurrency(Math.abs(calculated_revenue_change))}`
                          : `${formatImpact(Math.abs(rating_change_sum))} ★`}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </>
          ) : null}

          {hasSpecificFilters && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', ml: 0, mt: 1 }}>
              <Box sx={{ width: '140px', flexShrink: 0 }}>
                <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: { xs: '1rem', md: '1.125rem' } }}>Filters:</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                <Typography variant="body1" sx={{ fontSize: { xs: '1rem', md: '1.125rem' } }}>
                  {getFilterDescriptionText()}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ReviewsOverview; 